import React, { useState } from 'react';
import { CircledExclamationPointIcon, EnvelopeIcon, LoaderIcon } from '@repo/icons';
import { useLocation } from 'react-router-dom';
import { apiRequest } from '@repo/utils/helpers/api';
import styles from './VerifyEmail.module.scss';
import { setVerifyEmailRedirect } from '@repo/utils/helpers/verify-email-redirect';
import { supportHref } from '@repo/utils/helpers/preferred-email-client';
import ViewWrapper from '../ViewWrapper';
import { useAuthContext } from '@repo/hooks';

export default function VerifyEmail() {
  const { user } = useAuthContext();
  const location = useLocation();
  const [didError, setDidError] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  async function verify(): Promise<void> {
    setVerifyEmailRedirect(location.pathname);
    setEmailSending(true);

    try {
      await apiRequest<any>('POST', '/verified-emails/add-email/', {}, { apiVersion: 'v2' });

      setEmailSent(true);
    } catch (e) {
      console.error(e);
      setDidError(true);
    }
  }

  const icon = () => {
    if (didError) {
      return <CircledExclamationPointIcon />;
    }

    return <EnvelopeIcon />;
  };

  const headline = () => {
    if (didError) {
      return 'Sorry, something went wrong';
    }

    return 'Verify Your Email';
  };

  const message = () => {
    if (didError) {
      return (
        <>
          Something went wrong.
          <br />
          Please get in touch for assistance.
        </>
      );
    }

    if (emailSent) {
      return (
        <>
          We just sent an email to
          <br />
          <b>{user.email}</b>.
          <br />
          Please use the link in that email to verify
          <br />
          your email and finish logging in. If you
          <br />
          need assistance, please
        </>
      );
    }

    return (
      <>
        You need to verify your email
        <br />
        to access Clay Community. We'll send
        <br /> an email to the preferred email you have
        <br />
        set in Clay when you click below.
        <br />
      </>
    );
  };

  const button = () => {
    if (didError || emailSent) {
      return {
        text: 'Reach Out',
        onClick: () => window.open(supportHref()),
      };
    }

    if (emailSending) {
      return {
        text: (
          <>
            Sending Email <LoaderIcon />
          </>
        ),
        onClick: () => null,
        className: styles.loadingBtn,
      };
    }

    return {
      text: 'Verify Email',
      onClick: verify,
    };
  };

  return <ViewWrapper icon={icon()} headline={headline()} message={message()} button={button()} />;
}
